/**
 * D-RNG allocation engine.
 *
 * Attributes dispensed volume back to individual dairies, and thus to fuel
 * pathway codes, so a quarter's reported therms can be defended to a verifier.
 * Seven certified dairies plus provisional ones feed one upgrading facility
 * through one injection meter; each carries its own pathway code and carbon
 * intensity, so "how much of this station's gas was Trinkler's" is a question
 * the filing has to answer.
 *
 * ## The algorithm
 *
 * A single-pass two-pointer merge. An ordered queue of dairy-vintage lots is
 * walked against an ordered list of station capacities, emitting one row at
 * each overlap. Every station ends exactly full; every fully-consumed lot ends
 * exactly empty; whatever the queue does not reach stays banked for next
 * period.
 *
 * ## Two orderings, both recovered rather than documented
 *
 * The two-sided constraint has many valid solutions, and these orderings pick
 * the one Aemetis Compliance actually produces. Both were derived by scoring
 * candidate rules against 17 periods of the source workbook, not from a spec:
 *
 *   - **Lots: dairy-major, then FIFO by vintage.** Emphatically *not* global
 *     FIFO. Sorting globally by vintage scores 11 mismatched dairy-months
 *     against the workbook; dairy-major scores 4. Dairy-major also explains
 *     two behaviours at once — a dairy drains its own oldest vintage first,
 *     *and* the dairy sorting last is the one starved when the monthly target
 *     falls short of production. In August 2025 that shortfall was 1,654.50
 *     MMBtu and Martin Bro's August production was 1,654.54.
 *   - **From 2025-11, oldest vintage first.** Banked lots are drawn ahead of
 *     the period's own production, oldest vintage first and roster order
 *     within a vintage. The customer changed method in November 2025: their
 *     D-RNG Dispensing Schedule allocates October dairy-major but opens
 *     November with Martin Bro's August lot, then September's, then October's.
 *     Against that schedule, Nov 2025 – May 2026 match 143 of 145 rows under
 *     this rule and 63 of 169 under dairy-major. Mirrors `CARRIED_FIRST_FROM`
 *     in the backend's `compliance/lcfs/allocation.py`.
 *   - **Stations: the order they arrive in the dispensing feed.** Output
 *     therefore depends on input row order. That is reproducible but fragile,
 *     so the received order is carried on the capacity records rather than
 *     re-derived, and a reordered feed must not silently re-allocate.
 *
 * ## What it does not yet settle
 *
 * Four dairy-months out of 208 still differ from the workbook, all of them a
 * single 128.62 MMBtu lot swapping between Borba and Martin Bro's at a
 * capacity boundary. That is a tie-break rule 17 periods of data cannot
 * distinguish, and it is an open question for Compliance rather than something
 * to guess at. It moves nothing at pathway level, which is the grain that gets
 * reported.
 *
 * Every figure is a `Decimal`. No allocation quantity passes through a float —
 * the workbook's own stranded 0.52 MMBtu remainder is what float arithmetic
 * looks like after a year.
 */

import { Decimal } from "@compliance/domain";

export type Lot = {
  dairyId: string;
  /** Calendar month the volume was produced, `YYYY-MM`. */
  vintage: string;
  /** The vintage's lifetime production — constant once the lot exists. */
  produced: Decimal;
  /**
   * Balance at the start of the period being allocated.
   *
   * Distinct from `produced` and the reason this field exists: for a lot
   * carried in from an earlier period, `produced` is the whole vintage's output
   * while everything else on the lot is scoped to this period. A screen that
   * shows `produced` next to a period-scoped `consumed` reads as if volume
   * vanished. `opening − consumed === remaining`, always.
   */
  opening: Decimal;
  /** Consumed during the period being allocated. */
  consumed: Decimal;
  remaining: Decimal;
};

export type Capacity = {
  station: string;
  aemetisFseId: string;
  mmbtu: Decimal;
  feedOrder: number;
};

export type AllocationRow = {
  period: string;
  station: string;
  aemetisFseId: string;
  dairyId: string;
  vintage: string;
  mmbtu: Decimal;
};

export type AllocationResult = {
  period: string;
  rows: AllocationRow[];
  /** Per-station unallocated remainder. Zero everywhere is the healthy state. */
  stationResiduals: { station: string; aemetisFseId: string; residual: Decimal }[];
  /** Lot balances after the period, including untouched banked volume. */
  lots: Lot[];
  allocated: Decimal;
  capacity: Decimal;
  /**
   * The lot queue emptied before every station was filled.
   *
   * This distinguishes the two reasons a station can carry a residual, and the
   * UI must not report them the same way. With volume still banked, a residual
   * means the allocation is broken. With the queue exhausted, it means the
   * dairies did not produce enough to cover what was claimed at the stations —
   * a real operational condition, and a question about the claim rather than a
   * defect in the arithmetic.
   */
  queueExhausted: boolean;
  /** Volume still banked after the period — the opening balance for the next. */
  banked: Decimal;
};

const ZERO = new Decimal(0);

/** First period whose queue draws carried lots before current production. */
export const CARRIED_FIRST_FROM = "2025-11";

/**
 * Lot queue order: dairy roster position, then oldest vintage first — and from
 * {@link CARRIED_FIRST_FROM}, oldest vintage first, then roster position.
 */
export function compareLots(
  a: Pick<Lot, "dairyId" | "vintage">,
  b: Pick<Lot, "dairyId" | "vintage">,
  rosterPosition: (dairyId: string) => number,
  period?: string,
): number {
  if (period !== undefined && period >= CARRIED_FIRST_FROM && a.vintage !== b.vintage) {
    return a.vintage < b.vintage ? -1 : 1;
  }
  const byDairy = rosterPosition(a.dairyId) - rosterPosition(b.dairyId);
  if (byDairy !== 0) return byDairy;
  return a.vintage < b.vintage ? -1 : a.vintage > b.vintage ? 1 : 0;
}

/**
 * Allocate one period.
 *
 * `openingLots` is carried in rather than recomputed so a period can be
 * replayed from a known opening balance — which is what invariant I5
 * (inventory continuity) actually asserts, and what makes a restatement of one
 * period possible without recomputing history.
 */
export function allocatePeriod(input: {
  period: string;
  openingLots: readonly Lot[];
  /** Production booked to this period, becoming new lots at this vintage. */
  production: readonly { dairyId: string; mmbtu: Decimal }[];
  capacities: readonly Capacity[];
  rosterPosition: (dairyId: string) => number;
}): AllocationResult {
  const { period, openingLots, production, capacities, rosterPosition } = input;

  // Opening balances, plus this period's production as fresh lots at this
  // vintage. A dairy that produced in a month it already has banked volume for
  // adds to that lot rather than creating a second one at the same vintage.
  const lots: Lot[] = openingLots.map((l) => ({ ...l }));
  for (const p of production) {
    if (p.mmbtu.lte(ZERO)) continue;
    const existing = lots.find((l) => l.dairyId === p.dairyId && l.vintage === period);
    if (existing) {
      existing.produced = existing.produced.plus(p.mmbtu);
      existing.remaining = existing.remaining.plus(p.mmbtu);
      existing.opening = existing.remaining;
    } else {
      lots.push({
        dairyId: p.dairyId,
        vintage: period,
        produced: p.mmbtu,
        opening: p.mmbtu,
        consumed: ZERO,
        remaining: p.mmbtu,
      });
    }
  }

  // Snapshot the opening balance after production is merged and before a single
  // therm is consumed, so it is right for both a fresh lot and a carried one.
  for (const lot of lots) lot.opening = lot.remaining;

  const queue = lots
    .filter((l) => l.remaining.gt(ZERO))
    .sort((a, b) => compareLots(a, b, rosterPosition, period));

  const stations = [...capacities]
    .sort((a, b) => a.feedOrder - b.feedOrder)
    .map((c) => ({ ...c, remaining: c.mmbtu }));

  const rows: AllocationRow[] = [];
  let i = 0;
  let j = 0;

  while (i < stations.length && j < queue.length) {
    const station = stations[i];
    const lot = queue[j];
    const take = Decimal.min(station.remaining, lot.remaining);

    // A zero take would emit a meaningless row and, worse, fail to advance
    // either pointer — the loop below only advances on an exhausted side.
    if (take.gt(ZERO)) {
      rows.push({
        period,
        station: station.station,
        aemetisFseId: station.aemetisFseId,
        dairyId: lot.dairyId,
        vintage: lot.vintage,
        mmbtu: take,
      });
      station.remaining = station.remaining.minus(take);
      lot.remaining = lot.remaining.minus(take);
      lot.consumed = lot.consumed.plus(take);
    }

    if (station.remaining.lte(ZERO)) i += 1;
    if (lot.remaining.lte(ZERO)) j += 1;
  }

  const retained = lots.filter((l) => l.remaining.gt(ZERO) || l.consumed.gt(ZERO));

  return {
    period,
    rows,
    stationResiduals: stations.map((s) => ({
      station: s.station,
      aemetisFseId: s.aemetisFseId,
      residual: s.remaining,
    })),
    lots: retained,
    allocated: rows.reduce((acc, r) => acc.plus(r.mmbtu), ZERO),
    capacity: capacities.reduce((acc, c) => acc.plus(c.mmbtu), ZERO),
    // j walked off the end of the queue rather than the loop ending because
    // every station filled.
    queueExhausted: j >= queue.length && i < stations.length,
    banked: retained.reduce((acc, l) => acc.plus(l.remaining), ZERO),
  };
}

/**
 * Run every period in order, carrying lot balances forward.
 *
 * Periods must be replayed from the beginning because a period's opening
 * balance is the previous period's close — there is no way to allocate
 * 2026-03 correctly without having allocated everything before it.
 */
export function runAllocation(input: {
  periods: readonly string[];
  production: readonly { dairyId: string; month: string; mmbtu: Decimal }[];
  capacities: readonly (Capacity & { period: string })[];
  rosterPosition: (dairyId: string) => number;
}): Map<string, AllocationResult> {
  const { periods, production, capacities, rosterPosition } = input;
  const out = new Map<string, AllocationResult>();
  let carried: Lot[] = [];

  for (const period of periods) {
    const result = allocatePeriod({
      period,
      openingLots: carried,
      production: production.filter((p) => p.month === period),
      capacities: capacities.filter((c) => c.period === period),
      rosterPosition,
    });
    out.set(period, result);
    // Only unconsumed volume carries; a drained lot leaves the bank entirely.
    // Only unconsumed volume carries. `opening` is re-snapshotted by the next
    // period, so it is left alone here.
    carried = result.lots
      .filter((l) => l.remaining.gt(ZERO))
      .map((l) => ({ ...l, consumed: ZERO }));
  }

  return out;
}

/** Vintage age in whole periods, for surfacing lots that have sat too long. */
export function vintageAge(vintage: string, asOf: string): number {
  const [vy, vm] = vintage.split("-").map(Number);
  const [ay, am] = asOf.split("-").map(Number);
  if (![vy, vm, ay, am].every(Number.isFinite)) return 0;
  return (ay - vy) * 12 + (am - vm);
}
